import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Input,
  Spin,
  notification,
} from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { apiRequest } from "util/services";
import {
  resolveMarketIntelligencePreviewBrands,
  resolveBrandLogoUrl,
} from "pages/IndustryDashboard/marketIntelligenceLockPreview";
import styles from "./style.module.scss";

const emptyBrand = () => ({ name: "", logoUrl: "" });

const Representation = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [brands, setBrands] = useState([]);
  const [defaultBrands, setDefaultBrands] = useState([]);
  const [industryName, setIndustryName] = useState(null);
  const [industryKey, setIndustryKey] = useState(null);
  const [maxVisible, setMaxVisible] = useState(6);
  const [enterpriseId, setEnterpriseId] = useState(null);
  const [enterpriseName, setEnterpriseName] = useState(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setForbidden(false);
    try {
      const res = await apiRequest(
        "industry_analysis/lock-preview/settings",
        "GET",
        null,
        true
      );
      if (res?.error) {
        if (String(res.error).toUpperCase().includes("FORBIDDEN")) {
          setForbidden(true);
          return;
        }
        notification.error({ message: res.error });
        return;
      }
      const payload = res?.success || {};
      setBrands(
        Array.isArray(payload.brands) && payload.brands.length
          ? payload.brands.map((b) => ({
              name: b.name || "",
              logoUrl: b.logoUrl || "",
            }))
          : [emptyBrand()]
      );
      setDefaultBrands(Array.isArray(payload.default_brands) ? payload.default_brands : []);
      setIndustryName(payload.industry_name || null);
      setIndustryKey(payload.industry_key || null);
      setMaxVisible(payload.max_visible || 6);
      setEnterpriseId(payload.enterprise_id ?? null);
      setEnterpriseName(payload.enterprise_name || null);
    } catch (e) {
      notification.error({ message: String(e?.message || e) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const previewBrands = brands.filter((b) => String(b.name || "").trim());
  const fallbackPreview = resolveMarketIntelligencePreviewBrands(industryKey);
  const resolvedPreview = previewBrands.length
    ? previewBrands.slice(0, maxVisible)
    : fallbackPreview;

  const updateBrand = (index, field, value) => {
    setBrands((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const addBrand = () => {
    setBrands((prev) => [...prev, emptyBrand()]);
  };

  const removeBrand = (index) => {
    setBrands((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [emptyBrand()];
    });
  };

  const handleResetDefaults = () => {
    setBrands(
      defaultBrands.length
        ? defaultBrands.map((b) => ({ name: b.name || "", logoUrl: b.logoUrl || "" }))
        : [emptyBrand()]
    );
  };

  const handleClear = () => {
    setBrands([emptyBrand()]);
  };

  const handleSave = async () => {
    const normalized = brands
      .map((b) => ({
        name: String(b.name || "").trim(),
        logoUrl: String(b.logoUrl || "").trim() || null,
      }))
      .filter((b) => b.name);

    setSaving(true);
    try {
      const res = await apiRequest(
        "industry_analysis/lock-preview/settings",
        "PUT",
        { brands: normalized, enterprise_id: enterpriseId },
        true
      );
      if (res?.error) {
        notification.error({ message: res.error });
        return;
      }
      notification.success({ message: t("mi_preview_settings.saveSuccess") });
      await loadSettings();
    } catch (e) {
      notification.error({ message: String(e?.message || e) });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <Spin size="large" />
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className={styles.page}>
        <p className={styles.forbidden}>{t("mi_preview_settings.forbidden")}</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <p className={styles.breadcrumb}>{t("mi_preview_settings.breadcrumb")}</p>
        <h1>{t("mi_preview_settings.title")}</h1>
        <p className={styles.subtitle}>{t("mi_preview_settings.subtitle")}</p>
        {enterpriseName ? (
          <p className={styles.enterpriseBadge}>
            {t("mi_preview_settings.enterpriseHint", { enterprise: enterpriseName })}
          </p>
        ) : null}
        {industryName ? (
          <p className={styles.industryHint}>
            {t("mi_preview_settings.industryHint", { industry: industryName })}
          </p>
        ) : null}
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <h2>{t("mi_preview_settings.brandsTitle")}</h2>
          <p>{t("mi_preview_settings.brandsHint", { count: maxVisible })}</p>
        </div>

        <div className={styles.brandList}>
          {brands.map((brand, index) => (
            <div key={`brand-${index}`} className={styles.brandRow}>
              <Input
                value={brand.name}
                onChange={(e) => updateBrand(index, "name", e.target.value)}
                placeholder={t("mi_preview_settings.namePlaceholder")}
                className={styles.nameInput}
              />
              <Input
                value={brand.logoUrl}
                onChange={(e) => updateBrand(index, "logoUrl", e.target.value)}
                placeholder={t("mi_preview_settings.logoPlaceholder")}
                className={styles.logoInput}
              />
              <Button
                type="text"
                icon={<DeleteOutlined />}
                onClick={() => removeBrand(index)}
                aria-label={t("mi_preview_settings.removeBrand")}
              />
            </div>
          ))}
        </div>

        <div className={styles.actions}>
          <Button icon={<PlusOutlined />} onClick={addBrand}>
            {t("mi_preview_settings.addBrand")}
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleResetDefaults}>
            {t("mi_preview_settings.useDefaults")}
          </Button>
          <Button onClick={handleClear}>{t("mi_preview_settings.clearAll")}</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
          >
            {t("mi_preview_settings.save")}
          </Button>
        </div>
      </div>

      <div className={styles.previewPanel}>
        <div className={styles.previewRow}>
          {resolvedPreview.map((brand) => {
            const logoUrl = resolveBrandLogoUrl(brand);
            return (
            <span key={brand.name} className={styles.previewBrand}>
              {logoUrl ? (
                <img key={logoUrl} src={logoUrl} alt="" className={styles.previewLogo} />
              ) : (
                <span>{brand.name}</span>
              )}
            </span>
            );
          })}
        </div>
        {!previewBrands.length ? (
          <p className={styles.previewNote}>{t("mi_preview_settings.emptyUsesDefaults")}</p>
        ) : null}
      </div>
    </div>
  );
};

export default Representation;
